import { createAsyncThunk } from "@reduxjs/toolkit";
import { instance } from "./../../axios/axios";

// Glitch handling (Levi, thread 327). Submit a glitch from the "Report Glitch"
// pop-out. Multipart body: title (required), description (optional), optional
// one-tap severity/area, auto-captured page/device context, and 0..N files under
// "upload" (multi-upload + paste-a-screenshot) — matches Portal-BFF POST
// /glitch/portal/report, which saves the files to the proof_of_completion GCS
// bucket and stores them on the glitch as permanent links. The Glitches page
// thunks (list/status/triage/note/delete/notify) live below.
//
// Every /glitch/portal/* route is checkUser-gated on the BFF, so each thunk must
// attach the logged-in user's bearer token. There is no global axios auth
// interceptor in this app — every authenticated call pulls token from
// getState().auth.token and sets Authorization itself (same pattern as
// MassMessageActions / userActions). Omitting it returns the BFF's
// "Missing token. Please provide a valid authentication token." (Levi, thread 335).
export const reportGlitch = createAsyncThunk(
  "glitch/report",
  async (formData, { rejectWithValue, getState }) => {
    try {
      const token = getState().auth.token;
      const { data } = await instance.post("/glitch/portal/report", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      return data;
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);

// Pull the same message out of an axios error every thunk shares.
const reject = (error, rejectWithValue) => {
  if (error.response && error.response.data?.message) {
    return rejectWithValue(error.response.data.message);
  }
  return rejectWithValue(error.message);
};

// Authorization header for the JSON (non-multipart) thunks.
const authHeaders = (getState) => ({
  headers: { Authorization: `Bearer ${getState().auth.token}` },
});

// The Glitches page — list (open + resolved, newest first). Matches Portal-BFF
// GET /glitch/portal/list -> { data: { open: [], resolved: [] } }.
export const listGlitches = createAsyncThunk(
  "glitch/list",
  async (_, { rejectWithValue, getState }) => {
    try {
      const { data } = await instance.get("/glitch/portal/list", authHeaders(getState));
      return data;
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);

// Set In Progress/Fixed/Duplicate/Skipped, or pass status=null to reopen (clear
// it). 'In Progress' stays in the open list; only Fixed/Duplicate/Skipped move
// to Resolved. POST /glitch/portal/:id/status.
export const setGlitchStatus = createAsyncThunk(
  "glitch/setStatus",
  async ({ id, status }, { rejectWithValue, getState }) => {
    try {
      const { data } = await instance.post(
        `/glitch/portal/${id}/status`,
        { status },
        authHeaders(getState)
      );
      return data;
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);

// Set any of severity / area / assignee on a glitch (only the fields you pass
// are changed; pass "" / null to clear one). POST /glitch/portal/:id/triage.
export const triageGlitch = createAsyncThunk(
  "glitch/triage",
  async ({ id, ...fields }, { rejectWithValue, getState }) => {
    try {
      const { data } = await instance.post(
        `/glitch/portal/${id}/triage`,
        fields,
        authHeaders(getState)
      );
      return data;
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);

// Append a note to a glitch's running log. POST /glitch/portal/:id/note.
export const addGlitchNote = createAsyncThunk(
  "glitch/addNote",
  async ({ id, body, parentId, notify, recipients }, { rejectWithValue, getState }) => {
    try {
      // Thread fields (thread #3099): parentId = the response being replied
      // to; notify = save AND notify in one call (Send & Notify). Each is only
      // sent when set, so a plain note is the same request it always was.
      const payload = { body };
      if (parentId != null) payload.parentId = parentId;
      if (notify) payload.notify = true;
      if (notify && recipients) payload.recipients = recipients;
      const { data } = await instance.post(
        `/glitch/portal/${id}/note`,
        payload,
        authHeaders(getState)
      );
      return data;
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);

// Hard delete — removes the row AND its GCS file(s). DELETE /glitch/portal/:id.
export const deleteGlitch = createAsyncThunk(
  "glitch/delete",
  async (id, { rejectWithValue, getState }) => {
    try {
      const { data } = await instance.delete(`/glitch/portal/${id}`, authHeaders(getState));
      return data;
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);

// Notify the submitter their glitch was fixed. POST /glitch/portal/:id/notify.
// Pass `note` (the text typed in the Notes section) to include it as the
// notification body and save it to the notes log.
//
// With the verify flow (Levi 2026-08-25, thread 1a039407345e0609): pass
// `question` (the custom is-it-fixed message) and `withResponse: true` so the
// notification carries Yes/No buttons. Answering pays the submitter 25 pts from
// Levi's wallet and highlights the glitch green/red.
export const notifyGlitchSubmitter = createAsyncThunk(
  "glitch/notify",
  async ({ id, note, question, withResponse, recipients }, { rejectWithValue, getState }) => {
    try {
      const body = {};
      if (note) body.note = note;
      if (question) body.question = question;
      if (withResponse) body.withResponse = true;
      // Who gets this (Levi, thread #1558, Glitch #577). The spec is passed
      // through untouched and resolved server-side by the same audienceService
      // the preview used — no filtering happens here, so the list Levi saw and
      // the list that gets pinged are the same list by construction. Omitted
      // entirely when the picker was not used, so the old submitter-only path
      // is byte-for-byte what it was.
      if (recipients) body.recipients = recipients;
      const { data } = await instance.post(
        `/glitch/portal/${id}/notify`,
        body,
        authHeaders(getState)
      );
      return data;
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);

// Broadcast "this glitch is fixed" to EVERYONE (the close-the-loop option that
// sits next to the submitter-only Notify). Same endpoint, with audience=everyone
// → the BFF sends to all active employees. POST /glitch/portal/:id/notify.
export const broadcastGlitchFixed = createAsyncThunk(
  "glitch/broadcastFixed",
  async (id, { rejectWithValue, getState }) => {
    try {
      const { data } = await instance.post(
        `/glitch/portal/${id}/notify`,
        { audience: "everyone" },
        authHeaders(getState)
      );
      return data;
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);

// Status-change audit log for the History tab.
// GET /glitch/portal/history?from=&to=&person=&status=&limit=
export const fetchGlitchHistory = createAsyncThunk(
  "glitch/history",
  async ({ from, to, person, status, limit } = {}, { rejectWithValue, getState }) => {
    try {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      if (person) params.set("person", person);
      if (status) params.set("status", status);
      if (limit) params.set("limit", limit);
      const qs = params.toString();
      const { data } = await instance.get(
        `/glitch/portal/history${qs ? `?${qs}` : ""}`,
        authHeaders(getState)
      );
      return data;
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);

// Fix Leaderboard — who marked the most glitches as Fixed (or any status) in a window.
// GET /glitch/portal/history/leaderboard?from=&to=&status=
export const fetchGlitchLeaderboard = createAsyncThunk(
  "glitch/leaderboard",
  async ({ from, to, status } = {}, { rejectWithValue, getState }) => {
    try {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      if (status) params.set("status", status);
      const qs = params.toString();
      const { data } = await instance.get(
        `/glitch/portal/history/leaderboard${qs ? `?${qs}` : ""}`,
        authHeaders(getState)
      );
      return data;
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);

// "My Glitches" (thread 1a0588f4a47ab68c, Phase 1) — the caller's OWN glitches,
// split open + resolved, same shape as listGlitches. Any logged-in employee can
// call it. GET /glitch/portal/mine.
export const fetchMyGlitches = createAsyncThunk(
  "glitch/mine",
  async (_, { rejectWithValue, getState }) => {
    try {
      const { data } = await instance.get("/glitch/portal/mine", authHeaders(getState));
      return data;
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);

// Edit a glitch's title / description / severity / area (submitter-own-open or
// admin). POST /glitch/portal/:id/update — only the fields you pass change.
export const updateGlitch = createAsyncThunk(
  "glitch/update",
  async ({ id, ...fields }, { rejectWithValue, getState }) => {
    try {
      const { data } = await instance.post(
        `/glitch/portal/${id}/update`,
        fields,
        authHeaders(getState)
      );
      return data;
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);

// Append files to an existing glitch ("report now, add screenshots/videos
// later"). Multipart body, field "upload". POST /glitch/portal/:id/attachments.
export const addGlitchAttachments = createAsyncThunk(
  "glitch/addAttachments",
  async ({ id, formData }, { rejectWithValue, getState }) => {
    try {
      const token = getState().auth.token;
      const { data } = await instance.post(`/glitch/portal/${id}/attachments`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      return data;
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);

// Remove ONE attachment from a glitch (edit-dialog attachment management,
// thread 1a05d8398e11e2a9 r3). DELETE /glitch/portal/:id/attachments/:attachmentId.
export const removeGlitchAttachment = createAsyncThunk(
  "glitch/removeAttachment",
  async ({ id, attachmentId }, { rejectWithValue, getState }) => {
    try {
      const { data } = await instance.delete(
        `/glitch/portal/${id}/attachments/${attachmentId}`,
        authHeaders(getState)
      );
      return data;
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);

// Submitter sends a free-text reply to whoever clicked Notify/Notify Again
// (thread #1338). Saves the reply as a notes row (reply_to_notify=1), clears
// has_pending_reply, and sends a notification back to the notifier.
// POST /glitch/portal/:id/reply-to-notify.
export const replyToGlitchNotify = createAsyncThunk(
  "glitch/replyToNotify",
  async ({ id, body }, { rejectWithValue, getState }) => {
    try {
      const { data } = await instance.post(
        `/glitch/portal/${id}/reply-to-notify`,
        { body },
        authHeaders(getState)
      );
      return data;
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);

// Admin clears the yellow pending-reply flag once they've read the response
// (thread #1338). PATCH /glitch/portal/:id/ack-reply.
export const ackGlitchReply = createAsyncThunk(
  "glitch/ackReply",
  async (id, { rejectWithValue, getState }) => {
    try {
      const { data } = await instance.patch(
        `/glitch/portal/${id}/ack-reply`,
        {},
        authHeaders(getState)
      );
      return data;
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);

// Privacy-safe aggregate for the "Top Bug Finders" board — per-submitter counts
// only (no titles/descriptions/notes). GET /glitch/portal/leaderboard.
export const fetchSubmitterLeaderboard = createAsyncThunk(
  "glitch/submitterLeaderboard",
  async (_, { rejectWithValue, getState }) => {
    try {
      const { data } = await instance.get("/glitch/portal/leaderboard", authHeaders(getState));
      return data;
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);

// Dispatch a glitch to Kai from the portal "Kai" button (Levi, 2026-09-01,
// thread #1299). Admin only on the BFF. POST /glitch/portal/:id/kai.
// Body: { model?, note? } — model pins the AI model for the dispatch; note is
// extra context for Kai.
// `pushFix` (Levi 2026-10-06, thread 1a112a1bf61cde09) asks Kai to merge the fix
// he writes instead of leaving the PR open, and to close the glitch when it
// lands. Only the President position may set it — the BFF 403s anyone else, so
// this thunk passes the flag through rather than deciding.
export const dispatchGlitchToKai = createAsyncThunk(
  "glitch/dispatchToKai",
  async ({ id, model, note, pushFix }, { rejectWithValue, getState }) => {
    try {
      const { data } = await instance.post(
        `/glitch/portal/${id}/kai`,
        { model: model || "", note: note || "", pushFix: Boolean(pushFix) },
        authHeaders(getState)
      );
      return data;
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);
